import { Card, CardContent, CardHeader, Grid, TextField } from '@mui/material'
import { CompanyType, JourneyCategory } from '@planning/app/api'
import { CompanyAutocomplete } from '@planning/components/autocompletes/company-autocomplete/CompanyAutocomplete'
import { useTranslate } from '@tolgee/react'
import { observer } from 'mobx-react-lite'
import { EditableField } from '../EditableField/EditableField'
import { LogisticsSubFormStore } from './LogisticsSubFormStore'

interface LogisticsSubFormSectionProps {
  store: LogisticsSubFormStore
  category?: JourneyCategory
}

export const LogisticsSubFormSection = observer(
  ({ store, category }: LogisticsSubFormSectionProps) => {
    const { t } = useTranslate()
    return (
      <Card variant='outlined'>
        <CardHeader title={t('logisticsInformation', 'Logistics information')} />
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {(category === JourneyCategory.Export ||
              category === JourneyCategory.Transshipment) && (
              <Grid size={{ xs: 12, md: 4 }}>
                <EditableField
                  label={t('shipper', 'Shipper')}
                  store={store.shipper}
                  dataCy='bulk-edit-shipper'
                >
                  {(value, onChange) => (
                    <CompanyAutocomplete
                      label={t('shipper', 'Shipper')}
                      dataCy='shipper-autocomplete'
                      selectedCompanyId={store.shipper.data?.id ?? null}
                      handleOnChange={company => onChange(company ?? null)}
                      filterByTypes={[CompanyType.Shipper]}
                    />
                  )}
                </EditableField>
              </Grid>
            )}

            {category !== JourneyCategory.Export && (
              <>
                <Grid size={{ xs: 12, md: 4 }}>
                  <EditableField
                    label={t('forwarder', 'Forwarder')}
                    store={store.forwarder}
                    dataCy='bulk-edit-forwarder'
                  >
                    {(value, onChange) => (
                      <CompanyAutocomplete
                        label={t('forwarder', 'Forwarder')}
                        dataCy='forwarder-autocomplete'
                        selectedCompanyId={store.forwarder.data?.id ?? null}
                        handleOnChange={company => onChange(company ?? null)}
                        filterByTypes={[CompanyType.Forwarder]}
                      />
                    )}
                  </EditableField>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <EditableField
                    label={t('consignee', 'Consignee')}
                    store={store.consignee}
                    dataCy='bulk-edit-consignee'
                  >
                    {(value, onChange) => (
                      <CompanyAutocomplete
                        label={t('consignee', 'Consignee')}
                        dataCy='consignee-autocomplete'
                        selectedCompanyShortName={value}
                        handleOnChange={consignee => onChange(consignee?.shortName ?? '')}
                        filterByTypes={[CompanyType.Consignee]}
                      />
                    )}
                  </EditableField>
                </Grid>
              </>
            )}
          </Grid>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <EditableField
                label={t('bookingReference', 'Booking reference')}
                store={store.bookingReference}
                dataCy='bulk-edit-booking-reference'
              >
                {(value, onChange) => (
                  <TextField
                    label={t('bookingReference', 'Booking reference')}
                    fullWidth
                    value={value ?? ''}
                    onChange={event => onChange(event.target.value)}
                  />
                )}
              </EditableField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <EditableField
                label={t('B/L', 'B/L')}
                store={store.billOfLadding}
                dataCy='bulk-edit-bill-of-ladding'
              >
                {(value, onChange) => (
                  <TextField
                    label={t('B/L', 'B/L')}
                    fullWidth
                    value={value ?? ''}
                    onChange={event => onChange(event.target.value)}
                  />
                )}
              </EditableField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    )
  },
)
